import { type FC, useEffect, type ComponentProps } from 'react';
import { observer } from 'mobx-react';
import translations from './.vocab';
import { useTranslations } from '@vocab/react';
import { Box, TextLinkButton, Text, Stack } from 'braid-design-system';
import debounce from 'lodash.debounce';
import { ProfileIcon } from 'src/shared/icons/ProfileIcon';
import { useStores } from 'src/utils/hooks/useStores';
import {
  type TrackDataRetentionCandidateListData,
  trackDataRetentionCandidatesHiddenDisplayedEvent,
  trackDataRetentionShowCandidatesPressedEvent,
} from 'src/utils/events';
import type { IJobStore } from 'src/stores/JobStore';
import type { IUserStore } from 'src/stores/UserStore';
import { TRACKING_CANDIDATE_LIST } from 'src/constants/pageTrackingData';
import * as styles from './OldJobEmptyState.css';

const dataRetentionCandidateListDataTrackingFactory = (
  seekAdvertiserId: string,
  userStore: IUserStore,
  jobStore: IJobStore,
): TrackDataRetentionCandidateListData => {
  const userId = Number(userStore.user.userId);
  const { jobId, listingStartedDate } = jobStore.job.jobInformation;
  const seekListingId = Number(jobId);
  return {
    seekAdvertiserId,
    userId,
    seekListingId,
    listingDate: listingStartedDate,
    currentPage: TRACKING_CANDIDATE_LIST,
    bucket: 'inbox',
  };
};

export const OldJobEmptyState: FC = () => {
  const { t } = useTranslations(translations);
  const { jobStore, seekAdvertiserId, userStore } = useStores();

  useEffect(() => {
    if (jobStore.job.showOldJobIntercom) {
      trackDataRetentionCandidatesHiddenDisplayedEvent(
        dataRetentionCandidateListDataTrackingFactory(
          seekAdvertiserId,
          userStore,
          jobStore,
        ),
      );
    }
  }, [seekAdvertiserId, userStore, jobStore]);

  return (
    <EmptyStateWrapper>
      <ProfileIcon />
      <Box maxWidth="medium">
        <Text weight="strong" size="large" align="center">
          {t('Candidate details are private')}
        </Text>
      </Box>
      <Box maxWidth="small" paddingX="large">
        <Box maxWidth="small" paddingX="large">
          <Text tone="secondary" align="center">
            {t(
              'We hide candidate details 2 years after a job ad is posted...',
              {
                TextLinkButton: (children) => (
                  <TextLinkButton
                    onClick={() => {
                      window.Intercom!('startSurvey', '23928508');
                      debounce(() => {
                        jobStore.job.markIntercomForOldJobAsVisited();
                      }, 1500)();
                      trackDataRetentionShowCandidatesPressedEvent(
                        dataRetentionCandidateListDataTrackingFactory(
                          seekAdvertiserId,
                          userStore,
                          jobStore,
                        ),
                      );
                    }}
                    weight="weak"
                    key="old-job-alert"
                  >
                    {children}
                  </TextLinkButton>
                ),
              },
            )}
          </Text>
        </Box>
      </Box>
    </EmptyStateWrapper>
  );
};

interface EmptyStateWrapperProps {
  children: ComponentProps<typeof Stack>['children'];
}
const EmptyStateWrapper: FC<EmptyStateWrapperProps> = observer(
  ({ children }) => (
    <Box
      background="surface"
      paddingX="xlarge"
      paddingY="medium"
      display="flex"
      justifyContent="center"
      alignItems="center"
      className={styles.emptyState}
    >
      <Stack space="large" align="center">
        {children}
      </Stack>
    </Box>
  ),
);
